import { alpha, Box, Button, Divider, Grid, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Page } from "@mb-pro-ui/components";
import { EnhancedTable, RemoteFilterViews, SimpleTextFilter, Filter as TableFilter, FilterView as TableFilterView } from "@mb-pro-ui/components/table";
import { Device, Firmware, FirmwareUpgradeState } from "../utils/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellProps, Column, Row } from "react-table";
import { FormatDateOptions, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { isEqual } from "lodash-es";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { usePageSxProps } from "./styles";
import { DeviceFilter, useServerCommunication } from "../utils/ServerCommunicaton";
import { CompanyAutocompleteFilter } from "../components/tablefilters/CompanyFilter";
import TitledCard from "../components/templates/basic/TitledCard";
import { FirmwareFilter } from "../components/tablefilters/FirmwareFilter";
import { useApi } from "../utils/ApiProvider";
import { FirmwareItem } from "../components/FirmwareItem";
import { useDebounce } from "../utils/utils";
import { StringId } from "../components/tablefilters/utils";
import { FilterViewProps } from "@mb-pro-ui/components/table/filters/base/types";
import { FirmwareUpdateDeviceItem, useUpdateStatusLabel } from "../components/FirmwareUpdateDeviceItem";
import { APP_NAME } from "../App";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { HardwareTypeAutocompleteFilter } from "../components/tablefilters/HardwareTypeFilter";

const limit = 100;

const dateFormatOptions: FormatDateOptions = {
  dateStyle: "short",
  timeStyle: "medium",
};

const DeviceAndFirmwareFilter = ({
  filterChanged,
  onFirmwareChanged,
}: {
  filterChanged: (filter: TableFilter[]) => void;
  onFirmwareChanged: (firmware: Firmware | null) => void;
}) => {
  const [_clearable, setClearable] = useState<boolean>();
  const onClearRef = useRef<() => void>();

  const { formatMessage } = useIntl();
  const api = useApi();

  const [filtersValue, setFiltersValue] = useState<TableFilter[]>([]);
  const [firmwareFilter, setFirmwareFilter] = useState<{ hwtype?: string; hwver?: number }>({});
  const debouncedFilters = useDebounce(filtersValue, 450);
  const debouncedFwFilters = useDebounce(firmwareFilter, 450);
  const filtersLoaded = useRef([false, false, false]);

  const { errorHandler: onError } = useErrorHandler([]);

  const getFirmwares = useCallback(() => {
    return api<Firmware[]>("/firmwares/list", {
      method: "post",
      body: JSON.stringify({ ...debouncedFwFilters, status: [-1, 0, 1, 2, 3] }),
    });
  }, [api, debouncedFwFilters]);
  const { mutate: getFirmwaresList, data: firmwaresData } = useMutation({ mutationFn: getFirmwares, onError });
  const firmwares = useMemo<StringId<Firmware>[] | undefined>(() => firmwaresData?.map(({ id, ...params }) => ({ id: `${id}`, ...params })), [firmwaresData]);

  const oneOfTheFiltersChanged = useCallback((keys: string[], filters: TableFilter[]) => {
    setFiltersValue((old) => {
      return [...old.filter(({ id }) => !keys.includes(id)), ...filters];
    });
  }, []);
  const filterChanged1 = useCallback(
    (filters: TableFilter[]) => {
      oneOfTheFiltersChanged(["hwtype", "hwver"], filters);
      filtersLoaded.current[0] = true;
      setFirmwareFilter((old) => {
        const newFilter: { hwtype?: string; hwver?: number } = {};

        filters.forEach(({ id, value }) => {
          switch (id) {
            case "hwtype":
              newFilter.hwtype = value;
              break;
            case "hwver":
              newFilter.hwver = parseInt(value);
              break;
          }
        });

        return isEqual(old, newFilter) ? old : newFilter;
      });
    },
    [oneOfTheFiltersChanged]
  );
  const filterChanged2 = useCallback(
    (filters: TableFilter[]) => {
      oneOfTheFiltersChanged(["unitid", "company", "account", "name", "description"], filters);
      filtersLoaded.current[1] = true;
    },
    [oneOfTheFiltersChanged]
  );
  const filterChanged3 = useCallback(
    (filters: TableFilter[]) => {
      if (!firmwares) {
        return;
      }
      const selected = firmwares?.find(({ id }) => id === filters[0]?.value);
      onFirmwareChanged(selected ? ({ ...selected, id: parseInt(selected.id) } as Firmware) : null);
      oneOfTheFiltersChanged(["firmware"], filters);
      filtersLoaded.current[2] = true;
    },
    [firmwares, onFirmwareChanged, oneOfTheFiltersChanged]
  );

  const firmwareAndDeviceFilters = useMemo(
    (): TableFilterView[] => [
      {
        id: "hwtype",
        Filter: HardwareTypeAutocompleteFilter,
      },
      {
        id: "hwver",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Hardware version" }) }),
      },
    ],
    [formatMessage]
  );

  const deviceFilter = useMemo(
    (): TableFilterView[] => [
      {
        id: "unitid",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Helios ID" }) }),
      },
      {
        id: "company",
        Filter: CompanyAutocompleteFilter,
      },
      {
        id: "account",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Account" }) }),
      },
      {
        id: "name",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Name" }) }),
      },
      {
        id: "description",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Description" }) }),
      },
    ],
    [formatMessage]
  );

  const FirmwareFilterComponent = useMemo(() => (props: FilterViewProps) => <FirmwareFilter {...props} firmwares={firmwares ?? []} />, [firmwares]);

  const firmwareSelectFilter = useMemo(
    (): TableFilterView[] => [
      {
        id: "firmware",
        Filter: FirmwareFilterComponent,
      },
    ],
    [FirmwareFilterComponent]
  );

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Firmware update" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    if (filtersLoaded.current.some((i) => !i)) {
      return;
    }
    filterChanged(debouncedFilters);
  }, [debouncedFilters, filterChanged]);

  useEffect(() => {
    if (!filtersLoaded.current[0]) {
      return;
    }
    getFirmwaresList();
  }, [getFirmwaresList, debouncedFwFilters]);

  return (
    <TitledCard headerSx={{ display: "none" }}>
      <Typography variant="subtitle2" sx={{ mx: 1, mt: 1 }} color="primary">
        {formatMessage({ defaultMessage: "Filter devices and firmwares" })}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
        <RemoteFilterViews setClearable={setClearable} onClearRef={onClearRef} tableFilters={firmwareAndDeviceFilters} onFilterChange={filterChanged1} />
      </Box>
      <Divider />
      <Typography variant="subtitle2" sx={{ mx: 1, mt: 1 }} color="primary">
        {formatMessage({ defaultMessage: "Filter devices" })}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
        <RemoteFilterViews setClearable={setClearable} onClearRef={onClearRef} tableFilters={deviceFilter} onFilterChange={filterChanged2} />
      </Box>
      <Divider />
      <Typography variant="subtitle2" sx={{ mx: 1, mt: 1 }} color="primary">
        {formatMessage({ defaultMessage: "Select firmware (locally filter devices and selected devices lists)" })}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
        <RemoteFilterViews setClearable={setClearable} onClearRef={onClearRef} tableFilters={firmwareSelectFilter} onFilterChange={filterChanged3} />
      </Box>
    </TitledCard>
  );
};

export const FirmwareUpdatePage = () => {
  const [filter, setFilter] = useState<DeviceFilter | null>(null);
  const [updateStatuses, setUpdateStatuses] = useState<{ [unitid: string]: FirmwareUpgradeState | null }>({});
  const queryControllerRef = useRef<AbortController | null>(null);

  const [selectedDevices, setSelectedDevices] = useState<Device[]>([]);
  const [selectedFirmware, setSelectedFirmware] = useState<Firmware | null>(null);

  const theme = useTheme();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const narrow = useMediaQuery("(max-width:1200px)", { noSsr: true });
  const api = useApi();

  const { getDeviceList } = useServerCommunication();

  const refreshUpdateStatus = useCallback(
    (unitid: number) => {
      return api<FirmwareUpgradeState>(`/units/${unitid}/firmware/status`).then((status) =>
        setUpdateStatuses((old) => {
          old[unitid] = status;
          return { ...old };
        })
      );
    },
    [api]
  );

  const { errorHandler: onError } = useErrorHandler([]);

  const getDevicesQuery = useCallback(() => {
    if (!filter) {
      return Promise.resolve(null);
    }
    if (queryControllerRef.current) {
      queryControllerRef.current.abort("outdated");
    }
    queryControllerRef.current = new AbortController();
    return getDeviceList({ ...filter, limit, joinFwUpdate: true, signal: queryControllerRef.current.signal }).catch((err) => {
      if (err.message !== "outdated") {
        throw err;
      }
    });
  }, [filter, getDeviceList]);
  const { mutate: getDevices, data: devices, status: queryStatus } = useMutation({ mutationFn: getDevicesQuery, onError });

  const filteredDevices = useMemo(
    () =>
      devices?.filter(
        (d) => !selectedDevices.find(({ unitid }) => d.unitid === unitid) && (!selectedFirmware || d.unitDetails?.hwtype === selectedFirmware.hwtype)
      ) ?? [],
    [devices, selectedDevices, selectedFirmware]
  );

  const filteredSelectedDevices = useMemo(
    () => selectedDevices.filter((d) => !selectedFirmware || d.unitDetails?.hwtype === selectedFirmware.hwtype),
    [selectedDevices, selectedFirmware]
  );

  const getStyle = useCallback(
    (_item: Device) => {
      let color = theme.palette.common.black;
      let backgroundColor = theme.palette.common.white;

      return { color, backgroundColor };
    },
    [theme]
  );

  const rowProps = useCallback(
    (row: Row<Device>) => ({
      style: {
        ...getStyle(row.original),
        "&:hover": {
          backgroundColor: (theme: any) => `${alpha(theme.palette.primary.main, 0.2)}`,
        },
      },
    }),
    [getStyle]
  );

  const filterChanged = useCallback((filter: TableFilter[]) => {
    setFilter((old) => {
      const newFilter = { withTemplate: true } as DeviceFilter;
      filter.forEach(({ id, value }) => {
        if (id === "without-template" && value) {
          delete newFilter.withTemplate;
        }
        if (["unitid", "hwtype", "company", "account", "name", "description"].includes(id)) {
          newFilter[id as keyof DeviceFilter] = value;
        }
        if (id === "hwver") {
          newFilter.hwver = parseInt(value);
        }
      });
      return isEqual(old, newFilter) ? old : newFilter;
    });
  }, []);

  const addToSelected = useCallback((device: Device | Device[]) => {
    setSelectedDevices((old) => {
      const devices = Array.isArray(device) ? device : [device];
      console.log("add to selected", ...devices);
      old.push(...devices);

      return [...old];
    });
  }, []);

  const removeFromSelected = useCallback((device: Device) => {
    setSelectedDevices((old) => {
      const index = old.findIndex(({ unitid }) => unitid === device.unitid);
      old.splice(index, 1);

      return [...old];
    });
  }, []);

  const startUpdates = useCallback(async () => {
    if (!selectedFirmware) {
      return;
    }
    for (let i = 0; i < filteredSelectedDevices.length; i++) {
      try {
        await api(`/units/${filteredSelectedDevices[i].unitid}/firmware/update`, { method: "post", body: JSON.stringify({ firmwareId: selectedFirmware.id }) });
        refreshUpdateStatus(filteredSelectedDevices[i].unitid);
      } catch (err) {
        throw err;
      }
    }
  }, [api, filteredSelectedDevices, refreshUpdateStatus, selectedFirmware]);

  const clearSelected = useCallback(() => {
    setSelectedDevices([]);
  }, []);

  const columnsNarrow = useMemo(
    (): Column<Device>[] => [
      {
        id: "ids",
        Header: formatMessage({
          defaultMessage: "IDs",
        }),
        accessor: (item) => [item.unitid, item.accountinfo?.account].filter((i) => !!i).join(" "),
        Cell: ({ row }: CellProps<Device>) => (
          <span style={{ height: 48, minHeight: 48 }}>
            <Typography component={"span"} sx={{ display: "block" }}>
              {row.original.unitid}
            </Typography>
            {row.original.accountinfo && (
              <Typography component={"span"} sx={{ display: "block" }}>
                {row.original.accountinfo.account}
              </Typography>
            )}
          </span>
        ),
        width: 75,
      },
      // {
      //   id: "device",
      //   Header: formatMessage({
      //     defaultMessage: "Device",
      //   }),
      //   accessor: (item) => [item.accountinfo?.name, item.accountinfo?.description].filter((i) => !!i).join(" "),
      //   Cell: ({ row }: CellProps<Device>) => (
      //     <span style={{ height: 48, minHeight: 48 }}>
      //       {row.original.accountinfo && (
      //         <>
      //           <Typography component={"span"} sx={{ display: "block" }}>
      //             {row.original.accountinfo.name}
      //           </Typography>
      //           <Typography component={"span"} sx={{ display: "block" }}>
      //             {row.original.accountinfo.description}
      //           </Typography>
      //         </>
      //       )}
      //     </span>
      //   ),
      //   width: 100,
      // },
      {
        id: "hwtype",
        Header: formatMessage({
          defaultMessage: "Hardware type",
        }),
        accessor: (item) => (item.unitDetails ? [item.unitDetails.hwtype, item.unitDetails.hwver].filter((i) => !!i).join(".") : null),
        width: 120,
      },
      {
        id: "version",
        Header: formatMessage({
          defaultMessage: "Software version",
        }),
        accessor: (item) => item.unitDetails?.swver ?? null,
        Cell: (data: CellProps<Device>) => <SwCell cellProps={data} />,
        width: 120,
      },
      {
        id: "company",
        Header: formatMessage({
          defaultMessage: "Owner company",
        }),
        accessor: (item) => item.company?.name,
        Cell: ({ row }: CellProps<Device>) => (
          <Box sx={{ display: "flex", flexDirection: "row", flex: 1, overflow: "hidden" }}>
            <span style={{ height: 52, minHeight: 52, overflow: "hidden" }}>
              <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.original.company?.name}
              </Typography>
            </span>
            <Box sx={{ flex: 1 }} />
            {/* <Button variant="text" color="secondary" endIcon={<ArrowRightAltIcon />} onClick={() => addToSelected(row.original)}>
              {formatMessage({ defaultMessage: "Select" })}
            </Button> */}
          </Box>
        ),
        width: 180,
      },
    ],
    [formatMessage]
  );

  const columns = useMemo(
    (): Column<Device>[] => [
      {
        id: "unitid",
        Header: formatMessage({
          defaultMessage: "Helios ID",
        }),
        accessor: (item) => item.unitid,
        width: 75,
      },
      {
        id: "account",
        Header: formatMessage({
          defaultMessage: "Account",
        }),
        accessor: (item) => item.accountinfo?.account,
        width: 75,
      },
      {
        id: "device",
        Header: formatMessage({
          defaultMessage: "Device",
        }),
        accessor: (item) => [item.accountinfo?.name, item.accountinfo?.description].filter((i) => !!i).join(" "),
        Cell: ({ row }: CellProps<Device>) => (
          <span style={{ height: 52, minHeight: 52 }}>
            {row.original.unitDetails && (
              <Typography component={"span"} sx={{ display: "block" }}>
                {[row.original.unitDetails?.hwtype, row.original.unitDetails?.hwver].filter((i) => !!i).join(".")}
              </Typography>
            )}
          </span>
        ),
        width: 150,
      },
      {
        id: "version",
        Header: formatMessage({
          defaultMessage: "Software version",
        }),
        accessor: (item) => item.unitDetails?.swver ?? null,
        Cell: (data: CellProps<Device>) => <SwCell cellProps={data} />,
        width: 120,
      },
      {
        id: "company",
        Header: formatMessage({
          defaultMessage: "Owner company",
        }),
        accessor: (item) => item.company?.name,
        Cell: ({ row }: CellProps<Device>) => (
          <Box sx={{ display: "flex", flexDirection: "row", flex: 1, overflow: "hidden" }}>
            <span style={{ height: 52, minHeight: 52, overflow: "hidden" }}>
              <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.original.company?.name}
              </Typography>
            </span>
            <Box sx={{ flex: 1 }} />
            {/* <Button variant="text" color="secondary" endIcon={<ArrowRightAltIcon />} sx={{ overflow: "hidden" }} onClick={() => addToSelected(row.original)}>
              {formatMessage({ defaultMessage: "Select" })}
            </Button> */}
          </Box>
        ),
        width: 200,
      },
    ],
    [formatMessage]
  );

  const selectedColumns = useMemo(
    (): Column<Device>[] => [
      {
        id: "all",
        accessor: (item) => item.unitid,
        Cell: ({ row }: CellProps<Device>) => (
          <Grid container sx={{ height: 52, minHeight: 52, flexWrap: "nowrap" }}>
            <Grid item container spacing={0.5} sx={{ flex: 1, alignItems: "flex-end" }}>
              <Grid item>
                <Typography>{row.original.unitid}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {[row.original.unitDetails?.hwtype, row.original.unitDetails?.hwver].filter((i) => !!i).join(".")}
                </Typography>
              </Grid>
              <Grid container item>
                <Typography variant="body2" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {row.original.company?.name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (filter === null) {
      return;
    }
    getDevices();
  }, [getDevices, filter]);

  const deviceAndFirmwareFilter = useMemo(
    () => <DeviceAndFirmwareFilter filterChanged={filterChanged} onFirmwareChanged={setSelectedFirmware} />,
    [filterChanged]
  );

  return (
    <Page sx={singlePageSx}>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12}>
          {deviceAndFirmwareFilter}
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: "flex", height: 400, minHeight: "50vh" }}>
          <EnhancedTable
            columns={narrow ? columnsNarrow : columns}
            data={filteredDevices}
            showResultCount
            getRowProps={rowProps}
            queryStatus={queryStatus}
            overscanCount={5}
            rowHeight={52}
            limit={limit}
            postfix={
              filteredDevices.length > 0 && (
                <Button
                  variant="text"
                  endIcon={<ArrowRightAltIcon />}
                  sx={{ color: (theme) => theme.palette.primary.contrastText }}
                  onClick={() => addToSelected(filteredDevices)}
                >
                  {formatMessage({ defaultMessage: "Select all" })}
                </Button>
              )
            }
            actionPosition="right"
            actionCellWidth={120}
            action={({ original }) => (
              <Button variant="text" color="secondary" endIcon={<ArrowRightAltIcon />} sx={{ overflow: "hidden" }} onClick={() => addToSelected(original)}>
                {formatMessage({ defaultMessage: "Select" })}
              </Button>
            )}
            emptyMessage={formatMessage({ defaultMessage: "No data match your query or all are selected" })}
          />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: "flex", height: 400, minHeight: "50vh" }}>
          <EnhancedTable
            columns={selectedColumns}
            data={filteredSelectedDevices}
            renderMessage={(data) => <Typography sx={{ color: (theme) => theme.palette.primary.contrastText }}>{data.length}</Typography>}
            getRowProps={rowProps}
            overscanCount={5}
            rowHeight={52}
            disableGlobalFilter
            prefix={
              <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.primary.contrastText }}>
                {formatMessage({ defaultMessage: "Selected devices" })}
              </Typography>
            }
            postfix={
              filteredSelectedDevices.length > 0 && (
                <Button variant="text" endIcon={<ClearIcon />} sx={{ color: (theme) => theme.palette.primary.contrastText }} onClick={clearSelected}>
                  {formatMessage({ defaultMessage: "Clear all" })}
                </Button>
              )
            }
            actionPosition="right"
            actionCellWidth={60}
            action={({ original }) => (
              <IconButton sx={{ color: (theme) => theme.palette.secondary.main, mt: 0.75 }} onClick={() => removeFromSelected(original)}>
                <ClearIcon />
              </IconButton>
            )}
            sx={{ minWidth: 100 }}
            emptyMessage={formatMessage({ defaultMessage: "No device selected" })}
            hideHeader={true}
          />
        </Grid>
      </Grid>

      <TitledCard title={formatMessage({ defaultMessage: "Update firmware" })}>
        <Grid container spacing={1} sx={{ p: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Selected firmware" })}</Typography>
          </Grid>
          {selectedFirmware && (
            <Grid item xs={12}>
              <FirmwareItem item={selectedFirmware} />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Selected devices" })}</Typography>
          </Grid>
          {selectedDevices.map((device, i) => (
            <Grid item xs={12} key={`selected-device-${device.unitid}`}>
              {i > 0 && <Divider />}
              <FirmwareUpdateDeviceItem device={device} status={updateStatuses[device.unitid] ?? null} refreshStatus={refreshUpdateStatus} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button disabled={selectedDevices.length === 0 || !selectedFirmware} onClick={startUpdates}>
              {formatMessage({ defaultMessage: "Start firmware update" })}
            </Button>
          </Grid>
        </Grid>
      </TitledCard>
    </Page>
  );
};

const SwCell = ({ cellProps: { row } }: { cellProps: CellProps<Device> }) => {
  const [open, setOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);

  const toggleTooltip = useCallback(() => setOpen((old) => !old), []);
  const hoverTooltip = useCallback(() => setHoverOpen(true), []);
  const outTooltip = useCallback(() => setHoverOpen(false), []);

  const { formatDate } = useIntl();
  const { updateStatusLabel } = useUpdateStatusLabel();
  const theme = useTheme();

  const color = useMemo(() => {
    const updateStatus = row.original.update;
    if (!updateStatus) {
      return theme.palette.text.secondary;
    }
    switch (updateStatus.status) {
      case 4:
        return theme.palette.success.light;
      case 3:
        return theme.palette.error.light;
      case 2:
      case 1:
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  }, [row.original.update, theme]);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", flex: 1, overflow: "hidden" }}>
      {row.original.unitDetails?.swver && <Typography>{row.original.unitDetails.swver}</Typography>}
      {row.original.update && (
        <>
          <Box sx={{ flex: 1 }} />
          <IconButton sx={{ color }} onClick={toggleTooltip} onMouseOver={hoverTooltip} onMouseOut={outTooltip}>
            <Tooltip
              open={open || hoverOpen}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: (theme) => theme.palette.background.default,
                    color,
                    borderColor: color,
                    borderStyle: "solid",
                    borderWidth: 1,
                  },
                },
              }}
              title={
                <Stack>
                  <Typography variant="caption">{updateStatusLabel(row.original.update.status)}</Typography>
                  <Typography variant="caption">{formatDate(row.original.update.status_ts, dateFormatOptions)}</Typography>
                </Stack>
              }
            >
              <InfoOutlinedIcon />
            </Tooltip>
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default FirmwareUpdatePage;
